import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
  ApiLearnerFilePdfPreviewStatusChoices,
  LearnerFileType,
  useGenerateLearnerFilePdfPreviewMutation,
  useGetLearnerFilePdfPreviewStatusQuery,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { DocumentViewerPDF } from "@components/DocumentReviewModal/components/DocumentViewerPDF";

interface DocumentViewerXLSXProps {
  file: LearnerFileType;
  width: number;
}

// Module-level cache: file ID → { url, cachedAt }.
// Skips re-triggering conversion when the modal is closed and reopened.
// Entries expire after 45 minutes — well within the S3 signed URL lifetime.
const CACHE_TTL_MS = 45 * 60 * 1000;
const completedUrlCache = new Map<string, { url: string; cachedAt: number }>();
const getCachedUrl = (fileId: string): string | undefined => {
  const entry = completedUrlCache.get(fileId);
  if (!entry) return undefined;
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    completedUrlCache.delete(fileId);
    return undefined;
  }
  return entry.url;
};

const POLL_INTERVAL_MS = 3000;

const PreviewStatusMessage: React.FC<{ icon?: React.ReactNode; message: string }> = ({ icon, message }) => (
  <div className="flex flex-col items-center justify-center h-full gap-3 text-text-secondary">
    {icon}
    <p className="text-sm">{message}</p>
  </div>
);

export const DocumentViewerXLSX: React.FC<DocumentViewerXLSXProps> = ({ file, width }) => {
  const fileId = file.id;
  const cachedUrl = fileId ? getCachedUrl(fileId) : undefined;
  const queryClient = useQueryClient();

  const { mutate: generatePreview } = useGenerateLearnerFilePdfPreviewMutation();
  const generatePreviewRef = useRef(generatePreview);
  generatePreviewRef.current = generatePreview;

  // Tracks the last (fileId, status) pair we triggered generation for.
  // Prevents duplicate mutation calls on every poll cycle while status is still null/failed.
  // Using fileId in the key means navigating away and back resets the guard naturally.
  const lastTriggeredRef = useRef<string | null>(null);

  // Always fetch current status on mount — props may be stale.
  // Once completed or failed, stop polling.
  const { data: pollData } = useGetLearnerFilePdfPreviewStatusQuery(
    { fileId: fileId ?? "" },
    {
      enabled: !!fileId && !cachedUrl,
      refetchInterval: (query) => {
        if (query.state.data?.learnerFileById === null) return false;
        const status = (query.state.data?.learnerFileById as LearnerFileType | undefined)?.pdfPreviewStatus;
        if (
          status === ApiLearnerFilePdfPreviewStatusChoices.Completed ||
          status === ApiLearnerFilePdfPreviewStatusChoices.Failed
        )
          return false;
        return POLL_INTERVAL_MS;
      },
    },
  );

  const polledFile = pollData?.learnerFileById as LearnerFileType | undefined;
  // Use ternary (not ??) so an explicit null from the backend doesn't fall through to the stale prop value.
  const pdfPreviewStatus = polledFile != null ? polledFile.pdfPreviewStatus : file.pdfPreviewStatus;
  const pdfPreviewSignedUrl = polledFile != null ? polledFile.pdfPreviewSignedUrl : file.pdfPreviewSignedUrl;

  // Once the status query resolves, kick off conversion only if it's genuinely needed.
  // Using polledFile (not props) avoids acting on stale data.
  const statusKnown = !!pollData;
  useEffect(() => {
    if (!fileId || !statusKnown || getCachedUrl(fileId)) return;
    // learnerFileById === null means the file doesn't exist — don't retry
    if (pollData?.learnerFileById === null) return;
    const freshStatus = (pollData?.learnerFileById as LearnerFileType | undefined)?.pdfPreviewStatus;
    if (!freshStatus || freshStatus === ApiLearnerFilePdfPreviewStatusChoices.Failed) {
      // Guard against firing on every poll cycle: only trigger once per (fileId, status) pair.
      const triggerKey = `${fileId}:${freshStatus ?? "null"}`;
      if (lastTriggeredRef.current === triggerKey) return;
      lastTriggeredRef.current = triggerKey;
      generatePreviewRef.current(
        { learnerFileId: fileId },
        {
          // Invalidate only after the mutation lands so the refetch sees "generating"
          // (not the stale "failed"), allowing the poll interval to restart.
          onSuccess: () => queryClient.invalidateQueries({ queryKey: ["getLearnerFilePdfPreviewStatus", { fileId }] }),
          // Reset the trigger guard so the effect can retry if the mutation itself fails.
          onError: () => {
            lastTriggeredRef.current = null;
          },
        },
      );
    }
  }, [fileId, statusKnown, pollData, queryClient]);

  // Cache completed URL when polling detects it
  useEffect(() => {
    if (pdfPreviewStatus === ApiLearnerFilePdfPreviewStatusChoices.Completed && pdfPreviewSignedUrl && fileId) {
      completedUrlCache.set(fileId, { url: pdfPreviewSignedUrl, cachedAt: Date.now() });
    }
  }, [pdfPreviewStatus, pdfPreviewSignedUrl, fileId]);

  const resolvedUrl =
    cachedUrl ?? (pdfPreviewStatus === ApiLearnerFilePdfPreviewStatusChoices.Completed ? pdfPreviewSignedUrl : null);

  if (resolvedUrl) {
    const fileWithPdfPreviewUrl = { ...file, signedS3LearnerFile: resolvedUrl };
    return <DocumentViewerPDF file={fileWithPdfPreviewUrl} width={width} />;
  }

  if (pdfPreviewStatus === ApiLearnerFilePdfPreviewStatusChoices.Completed) {
    // Status is COMPLETED but URL is missing — avoid permanent spinner since polling has stopped.
    return (
      <PreviewStatusMessage
        icon={<ExclamationTriangleIcon className="h-8 w-8 text-red-400" />}
        message="Could not load the preview for this file."
      />
    );
  }

  if (pollData?.learnerFileById === null) {
    return (
      <PreviewStatusMessage
        icon={<ExclamationTriangleIcon className="h-8 w-8 text-red-400" />}
        message="File not found."
      />
    );
  }

  if (pdfPreviewStatus === ApiLearnerFilePdfPreviewStatusChoices.Failed) {
    return (
      <PreviewStatusMessage
        icon={<ExclamationTriangleIcon className="h-8 w-8 text-red-400" />}
        message="Could not generate a preview for this file."
      />
    );
  }

  return (
    <PreviewStatusMessage
      icon={<Loader className="w-6 h-6 mr-0 ml-0 text-text-secondary" />}
      message="Generating preview…"
    />
  );
};
